import React, { useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAtom, useAtomValue } from "jotai";
import { useAuth } from "../../context/AuthContext.tsx";
import * as S from "./cashFlow.style.ts";
import { budgetAtom } from "../../hook/BudgetAtom.ts";
import { incomeEventsAtom } from "../../hook/IncomeEventsAtom.ts";
import { recurringIncomeAtom } from "../../hook/RecurringIncomeAtom.ts";
import { cashAccountsAtom } from "../../hook/CashAccountsAtom.ts";
import { ownerPayConfigAtom } from "../../hook/OwnerPayConfigAtom.ts";
import { essentialOverridesAtom } from "../../hook/EssentialOverridesAtom.ts";
import { buildCashFlowSummary } from "../../functions/cashflow.ts";
import { modeLabels } from "../../functions/cashflowCopy.ts";
import { useMoney } from "../../hook/useMoney.ts";
import { persistCashAccounts } from "../../functions/cashflowSync.ts";
import { CashAccounts } from "../../types.ts";
import Button from "../../components/Button/Button.tsx";
import CashFlowChart from "../../components/CashFlowChart/CashFlowChart.tsx";
import { trackEvent } from "../../functions/mixpanel.ts";
import { userAtom } from "../../hook/UserAtom.ts";
import { getDateInfo } from "../../functions/helper.ts";

const CashFlow = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const budget = useAtomValue(budgetAtom);
  const currentUser = useAtomValue(userAtom);
  const incomeEvents = useAtomValue(incomeEventsAtom);
  const recurring = useAtomValue(recurringIncomeAtom);
  const [accounts, setAccounts] = useAtom(cashAccountsAtom);
  const config = useAtomValue(ownerPayConfigAtom);
  const overrides = useAtomValue(essentialOverridesAtom);
  const labels = modeLabels(config);
  const { money } = useMoney();

  const summary = useMemo(
    () =>
      buildCashFlowSummary(
        budget,
        incomeEvents,
        accounts,
        config,
        overrides,
        undefined,
        recurring,
      ),
    [budget, incomeEvents, accounts, config, overrides, recurring],
  );

  type BalanceField = "checkingBalance" | "bufferBalance" | "taxBalance";
  const [draft, setDraft] = useState<Partial<Record<BalanceField, string>>>({});

  const commit = (field: BalanceField, raw: string) => {
    const value = Number(raw);
    // Blank / non-numeric input: drop the edit rather than silently zero the
    // balance.
    if (raw.trim() === "" || Number.isNaN(value)) {
      setDraft((d) => {
        const copy = { ...d };
        delete copy[field];
        return copy;
      });
      return;
    }

    const next: CashAccounts = {
      ...accounts,
      [field]: value,
      asOf: new Date().toISOString().slice(0, 10),
    };

    setAccounts(next);
    persistCashAccounts(auth, next);
    trackEvent("Cash Flow - updated balance", { field });
    setDraft((d) => {
      const copy = { ...d };
      delete copy[field];
      return copy;
    });
  };

  const balanceField = (field: BalanceField, label: string) => (
    <S.EditRow>
      <label htmlFor={field}>{label}</label>
      <input
        id={field}
        type="number"
        value={draft[field] ?? String(accounts[field] ?? 0)}
        onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.value }))}
        onBlur={(e) => commit(field, e.target.value)}
      />
    </S.EditRow>
  );

  const hasIncome = incomeEvents.length > 0 || recurring.some((r) => r.active);
  const thisMonth = summary.projection[0];
  const runwayMonths = Math.min(summary.runway.months, 99);
  const runwayClass =
    runwayMonths >= config.bufferTargetMonths
      ? "good"
      : runwayMonths >= 1
        ? ""
        : "warn";
  const coveragePct = Math.min(
    100,
    Math.round(summary.leanCoverage.ratio * 100),
  );
  const drawGap = summary.recommendedDraw - config.monthlyDraw;
  const regular = config.mode === "regular";

  if (!currentUser?.hasBudget) {
    return <Navigate to="/onboarding" replace />;
  }

  const { currentMonth, currentYear } = getDateInfo();
  const totalExpense = summary.burn.essential;
  // This month's income only — not the whole projection horizon.
  const monthlyIncome = thisMonth?.inflow ?? 0;
  const totalIncome =
    monthlyIncome +
    Number(
      draft["checkingBalance"] ?? String(accounts["checkingBalance"] ?? 0),
    );
  const remainingCash = totalIncome - totalExpense;

  return (
    <S.Wrapper>
      <S.Intro>
        {regular
          ? "See how long your cash would last, what's safe to spend each month, and how your savings build over the year."
          : "Built for income that arrives in lumps. See how long your cash lasts, what you can safely pay yourself this month, and where the lean months are before they hit."}
      </S.Intro>

      {!hasIncome && (
        <S.EmptyState>
          <p>
            {regular
              ? "Add your paycheck so this page can show your runway, a monthly spending plan and how your savings grow."
              : "Add the payments you've received and the ones you expect, and this page fills in with your runway, a safe owner's pay and a month-by-month projection."}
          </p>
          <Button buttonSize="medium" handleClick={() => navigate("/income")}>
            Add income
          </Button>
        </S.EmptyState>
      )}

      <S.CardGrid>
        <S.CardWrapper>
          <S.Card className="wide">
            <h3>Checking</h3>
            {balanceField("checkingBalance", "Cash on hand")}
          </S.Card>
          <S.Card className="wide">
            <h3>{labels.bufferLabel}</h3>
            {balanceField("bufferBalance", labels.bufferSubLabel)}
            <span className="sub">
              Target {money(summary.bufferTarget)} ({config.bufferTargetMonths}{" "}
              mo of essentials)
            </span>
          </S.Card>
          {labels.showTaxCard && (
            <S.Card className="wide">
              <h3>Tax reserve</h3>
              {balanceField("taxBalance", "Set aside for taxes")}
              <span className="sub">
                Reserving {config.taxReservePercent}% of every payment
              </span>
            </S.Card>
          )}

          <S.Card className={`wide ${runwayClass}`}>
            <h3>Cash runway</h3>
            <span className="big">
              {runwayMonths >= 99 ? "99+" : runwayMonths.toFixed(1)} months
            </span>
            <span className="sub">
              Essentials ({money(totalExpense)}/mo) covered through{" "}
              {new Date(summary.runway.until).toLocaleDateString(undefined, {
                month: "long",
                year: "numeric",
              })}
            </span>
          </S.Card>
        </S.CardWrapper>

        <S.Card>
          <h3>{labels.planCardTitle}</h3>
          <S.Recommendation>
            <span>
              <span className="label">Income + Checkings</span>
              <br />
              <span className="value">{money(totalIncome)}</span>
            </span>
            <span>
              <span className="label">Expenses</span>
              <br />
              <span className="value">{money(totalExpense)}</span>
            </span>
            <span>
              <span className="label">Remaining Cash</span>
              <br />
              <span className="value">{money(remainingCash)}</span>
            </span>
          </S.Recommendation>
          <S.Recommendation>
            <span>
              <span className="label">{labels.safeAmountLabel}</span>
              <br />
              <span className="value">{money(summary.recommendedDraw)}</span>
            </span>
            <span>
              <span className="label">{labels.currentAmountLabel}</span>
              <br />
              <span className="value">{money(config.monthlyDraw)}</span>
            </span>
            {thisMonth && (
              <span>
                <span className="label">This month</span>
                <br />
                <span className="value">
                  {thisMonth.bufferContribution >= 0
                    ? `+${money(thisMonth.bufferContribution)} ${labels.contributionInVerb}`
                    : `${money(Math.abs(thisMonth.bufferContribution))} ${labels.contributionOutVerb}`}
                </span>
              </span>
            )}
          </S.Recommendation>
          <span className="sub">
            {drawGap > 1
              ? regular
                ? `You have about ${money(drawGap)}/mo of headroom over your budget.`
                : `You have room to raise your pay by about ${money(drawGap)}/mo.`
              : drawGap < -1
                ? regular
                  ? `Your budget runs about ${money(Math.abs(drawGap))}/mo above your take-home.`
                  : `Your current pay is about ${money(Math.abs(drawGap))}/mo above what's sustainable.`
                : regular
                  ? "Your budget lines up with your take-home pay."
                  : "Your pay is right in line with what your income supports."}
          </span>
          <S.Actions>
            <Button
              buttonSize="small"
              handleClick={() => navigate("/owner-pay")}
            >
              Adjust {labels.ownerPayPageTitle}
            </Button>
            <Button buttonSize="small" handleClick={() => navigate("/income")}>
              Manage Income
            </Button>
            <Button
              buttonSize="small"
              handleClick={() =>
                navigate(`/monthly/expense/${currentMonth}/${currentYear}`)
              }
            >
              Manage Expenses
            </Button>
          </S.Actions>
        </S.Card>

        {hasIncome && (
          <S.Card>
            <h3>Projection</h3>
            <CashFlowChart projection={summary.projection} />
          </S.Card>
        )}

        {labels.showLeanCard && (
          <S.Card>
            <h3>Lean month vs essentials</h3>
            <S.CoverageBar>
              <span
                className={summary.leanCoverage.covered ? "" : "short"}
                style={{ width: `${coveragePct}%` }}
              />
            </S.CoverageBar>
            <span className="sub">
              A lean month brings in about {money(summary.leanMonthIncome)}.
              Your essentials run {money(totalExpense)}.{" "}
              {summary.leanCoverage.covered
                ? "Even a slow month covers the basics."
                : "A slow month doesn't cover the basics — that's what the buffer is for."}
            </span>
          </S.Card>
        )}
      </S.CardGrid>
    </S.Wrapper>
  );
};

export default CashFlow;
